import React from "react";
import { HardHat, Wrench, Zap, Flame, Building2, ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * TargetAudience — "Kenelle Duunari sopii" section.
 * 5 industry cards guiding visitors to the right package via a soft CTA to pricing.
 */
export const TargetAudience = () => {
  const { t } = useLanguage();

  const AUDIENCES = [
    { id: "rakennus", icon: HardHat, k: "rakennus" },
    { id: "lvi", icon: Wrench, k: "lvi" },
    { id: "sahko", icon: Zap, k: "sahko" },
    { id: "nuohous", icon: Flame, k: "nuohous" },
    { id: "kiinteisto", icon: Building2, k: "kiinteisto" },
  ];

  const scrollToPricing = () => {
    const el = document.querySelector("#hinnasto");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="kenelle-sopii"
      data-testid="target-audience"
      className="relative py-20 md:py-28 bg-[#0F0F0F]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 border-2 border-[#FFEA00] bg-black px-3 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 bg-[#FFEA00] rounded-full pulse-dot" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFEA00]">
              {t("audience.eyebrow")}
            </span>
          </div>
          <h2 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tight uppercase">
            {t("audience.headline_1")}{" "}
            <span className="text-[#FFEA00]">{t("audience.headline_accent")}</span>
          </h2>
          <p className="mt-5 font-mono text-sm md:text-[15px] text-[#A3A3A3] leading-relaxed max-w-2xl">
            {t("audience.subline")}
          </p>
        </div>

        {/* Grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
          {AUDIENCES.map(({ id, icon: Icon, k }) => (
            <div
              key={id}
              data-testid={`audience-card-${id}`}
              className="group relative border-2 border-[#2a2a2a] bg-black p-5 hover:border-[#FFEA00] transition-all duration-200"
            >
              {/* Corner marker */}
              <span
                aria-hidden="true"
                className="absolute top-0 right-0 w-2 h-2 bg-[#2a2a2a] group-hover:bg-[#FFEA00] transition-colors"
              />

              {/* Icon */}
              <div className="w-11 h-11 border-2 border-[#FFEA00] bg-[#FFEA00]/[0.06] flex items-center justify-center mb-4 group-hover:bg-[#FFEA00]/[0.15] transition-colors">
                <Icon className="w-5 h-5 text-[#FFEA00] stroke-[1.75]" />
              </div>

              <h3 className="font-display font-bold text-white text-lg leading-tight tracking-tight uppercase">
                {t(`audience.cards.${k}.title`)}
              </h3>
              <p className="mt-2 font-mono text-[11px] text-[#A3A3A3] leading-relaxed">
                {t(`audience.cards.${k}.description`)}
              </p>

              {/* Suggested tier chip */}
              <div className="mt-4 inline-flex items-center gap-1.5 border border-[#FFEA00]/50 bg-black px-2 py-1">
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A3A3A3]">
                  {t("audience.suggested")}
                </span>
                <span className="font-display font-bold text-[10px] tracking-widest uppercase text-[#FFEA00]">
                  {t(`audience.cards.${k}.tier`)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            onClick={scrollToPricing}
            data-testid="audience-cta-pricing"
            className="group inline-flex items-center gap-2 bg-[#FFEA00] text-black border-2 border-[#FFEA00] px-6 py-3 font-display font-bold text-sm tracking-widest uppercase shadow-[4px_4px_0_0_rgba(0,0,0,0.4)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,0.5)] hover:-translate-y-0.5 transition-all"
          >
            {t("audience.cta")}
            <ArrowRight className="w-4 h-4 stroke-[3] transition-transform group-hover:translate-x-1" />
          </button>
          <span className="font-mono text-[11px] text-[#A3A3A3] tracking-wide uppercase">
            {t("audience.cta_note")}
          </span>
        </div>
      </div>
    </section>
  );
};
